import { ClosedPosition, OpenPosition } from '@monorepo/types';
import { PositionViewType, ProcessedPositionsData, ViewConfig } from '../types';

/**
 * Processes position data from multiple queries and combines them based on selected view.
 *
 * This pure function takes raw position data and loading states, then combines them
 * according to the selected view type. It implements smart loading logic that only
 * shows loading state when there's no data to display and relevant queries are loading.
 *
 * @param closedData - Data from closed positions query
 * @param openData - Data from open positions query
 * @param selectedView - Currently selected view type (OPEN, CLOSED, or BOTH)
 * @param isLoadingClosed - Loading state for closed positions query
 * @param isLoadingOpen - Loading state for open positions query
 * @returns Combined and processed position data with loading state
 *
 * @example
 * ```typescript
 * const result = processPositionsData(
 *   { results: [position1, position2], total: 2 },
 *   { results: [position3], total: 1 },
 *   PositionViewType.BOTH,
 *   false,
 *   false
 * );
 * // result: { positions: [position1, position2, position3], isLoading: false, totalCount: 3 }
 * ```
 */
export const processPositionsData = (
  closedData: { results: ClosedPosition[]; total: number } | undefined,
  openData: { results: OpenPosition[]; total: number } | undefined,
  selectedView: PositionViewType,
  isLoadingClosed: boolean,
  isLoadingOpen: boolean,
): ProcessedPositionsData => {
  const viewConfigs: ViewConfig[] = [
    {
      type: PositionViewType.CLOSED,
      data: closedData?.results || [],
      total: closedData?.total || 0,
      isLoading: isLoadingClosed,
    },
    {
      type: PositionViewType.OPEN,
      data: openData?.results || [],
      total: openData?.total || 0,
      isLoading: isLoadingOpen,
    },
  ];

  // Determine which views to include based on selected view
  const activeViews =
    selectedView === PositionViewType.BOTH
      ? viewConfigs
      : viewConfigs.filter((view) => view.type === selectedView);

  const positions = activeViews.flatMap((view) => view.data);
  const totalCount = activeViews.reduce((sum, view) => sum + view.total, 0);

  // Only show loading if we have no data to display AND any active view is loading
  const isLoading =
    positions.length === 0 && activeViews.some((view) => view.isLoading);

  return { positions, isLoading, totalCount };
};
